import type { OBB, Vec2 } from '../../geo/types';
import { obbCorners } from '../../geo/polygon';
import type { Lot } from './types';
import { lotFrame } from './lotFrame';
import type { LotPlacer } from './lotPlacer';
import { cellAt, centreOf, eachCellInObb } from './claimGrid';
import { CLAIM, type Claims } from './plotClaims';
import { rectanglesIn, type Extent, type Frame } from './claimRects';

/** How far behind a house's back wall a building on its plot starts and ends. */
export interface Span {
  readonly from: number;
  readonly to: number;
}

/** A house and its plot as its claim is built on. */
export interface ClaimPlot {
  readonly house: Lot;
  /** Whether the plot is built on: drawn by its district's fill, on a bank, or lined with kamers. */
  readonly built: boolean;
  /** Whether the plot is roofed through from its courtyard. */
  readonly through: boolean;
  readonly spans: readonly Span[];
}

/** A built plot's house frame and the depths its buildings take, for asking many points. */
interface Use {
  readonly frontMid: Vec2;
  readonly facing: Vec2;
  readonly depth: number;
  readonly spans: readonly Span[];
  /** Where the roofs of a plot roofed through begin; Infinity where it is not roofed through. */
  readonly roofed: number;
}

function useOf(plot: ClaimPlot | null): Use | null {
  if (!plot?.built) return null;
  const { frontMid, facing, depth } = lotFrame(plot.house);
  const near = Math.min(...plot.spans.map((s) => s.from));
  return { frontMid, facing, depth, spans: plot.spans, roofed: plot.through ? near : Infinity };
}

/*
 * Claimed ground takes the use of the plot ground beside it: beside the house it is built, and
 * beside a building on the plot or behind the courtyard of a plot roofed through; beside a courtyard
 * or a yard, before the house's front or on a plot not built on it stays open.
 */
function builds(use: Use, p: Vec2): boolean {
  const { frontMid, facing, depth } = use;
  const behind = (frontMid[0] - p[0]) * facing[0] + (frontMid[1] - p[1]) * facing[1] - depth;
  if (behind < -depth) return false;
  if (behind <= 0 || behind >= use.roofed) return true;
  return use.spans.some((s) => behind >= s.from && behind <= s.to);
}

function frameOf(house: Lot): Frame {
  const { frontMid, facing } = lotFrame(house);
  return { origin: frontMid, across: house.obb.axisU, back: [-facing[0], -facing[1]] };
}

/* The stretch of the house's frame its own built claim spans, widened by the least building so it may reach across its neighbour's half of a gap. */
function extentOf(frame: Frame, points: readonly Vec2[]): Extent {
  const us = points.map(
    (p) => (p[0] - frame.origin[0]) * frame.across[0] + (p[1] - frame.origin[1]) * frame.across[1]
  );
  const ts = points.map(
    (p) => (p[0] - frame.origin[0]) * frame.back[0] + (p[1] - frame.origin[1]) * frame.back[1]
  );
  const pad = CLAIM.least + CLAIM.cell;
  return {
    u0: Math.min(...us) - pad,
    u1: Math.max(...us) + pad,
    t0: Math.min(...ts) - CLAIM.cell,
    t1: Math.max(...ts) + CLAIM.cell,
  };
}

/* One side of a box moved out by `by`: sides 0 and 1 across it, 2 and 3 along it. */
function grown(obb: OBB, side: number, by: number): OBB {
  const [ax, ay] = obb.axisU;
  const dir: Vec2 = side < 2 ? [ax, ay] : [-ay, ax];
  const sign = side % 2 === 0 ? -1 : 1;
  return {
    center: [obb.center[0] + (dir[0] * sign * by) / 2, obb.center[1] + (dir[1] * sign * by) / 2],
    axisU: obb.axisU,
    halfU: obb.halfU + (side < 2 ? by / 2 : 0),
    halfV: obb.halfV + (side < 2 ? 0 : by / 2),
  };
}

/* The middle of a side of a box, a hair inside it. */
function sideMiddle(obb: OBB, side: number): Vec2 {
  const [ax, ay] = obb.axisU;
  const dir: Vec2 = side < 2 ? [ax, ay] : [-ay, ax];
  const reach = (side < 2 ? obb.halfU : obb.halfV) - 1e-3;
  const sign = side % 2 === 0 ? -1 : 1;
  return [obb.center[0] + dir[0] * sign * reach, obb.center[1] + dir[1] * sign * reach];
}

/*
 * A rectangle of cells is drawn in by the inset, which clears the lots it was sampled beside; then
 * each side runs out again, on claimed ground to be built, as far as the lots beside it allow, so
 * it stands wall to wall with them and no further into a courtyard than its cells reach.
 */
function snapped(obb: OBB, placer: LotPlacer, member: (p: Vec2) => boolean): OBB | null {
  let box: OBB = { ...obb, halfU: obb.halfU - CLAIM.inset, halfV: obb.halfV - CLAIM.inset };
  if (!placer.fits(box)) return null;
  for (let side = 0; side < 4; side++) {
    let [lo, hi] = [0, 3 * CLAIM.inset];
    for (let i = 0; i < 5; i++) {
      const mid = (lo + hi) / 2;
      const out = grown(box, side, mid);
      if (member(sideMiddle(out, side)) && placer.fits(out)) lo = mid;
      else hi = mid;
    }
    box = grown(box, side, lo);
  }
  return box;
}

/** What a building on claimed ground must fit: the lots standing, the yards the woodcut shows, the water. */
export interface ClaimSite {
  readonly placer: LotPlacer;
  readonly inYard: (p: Vec2) => boolean;
  readonly water: (p: Vec2) => boolean;
}

/* How far before a front its door's step lies, and where along the front a door may stand. */
const STEP = { out: 1, along: [-0.5, -0.25, 0, 0.25, 0.5], margin: 0.3 } as const;

function wetBefore(lot: Lot, water: (p: Vec2) => boolean): boolean {
  const { frontMid, facing, right, width } = lotFrame(lot);
  const span = Math.max(0, width - 2 * STEP.margin);
  return STEP.along.some((a) =>
    water([
      frontMid[0] + right[0] * a * span + facing[0] * STEP.out,
      frontMid[1] + right[1] * a * span + facing[1] * STEP.out,
    ])
  );
}

/*
 * A building on claimed ground faces the way its house faces, its door on the side toward the street;
 * where water lies before that side, as on a claim along a canal behind a corner house, it turns its
 * back to the water.
 */
function claimLot(
  house: Lot,
  k: number,
  obb: OBB,
  index: number,
  water: (p: Vec2) => boolean
): Lot {
  const lot: Lot = {
    index,
    sourceId: `${house.sourceId}/claim${k}`,
    side: house.side,
    ordinal: house.ordinal,
    kind: 'backhouse',
    obb,
    corners: obbCorners(obb),
    frontage: obb.halfU * 2,
    depth: obb.halfV * 2,
    facing: house.facing,
  };
  return wetBefore(lot, water) ? { ...lot, facing: [-house.facing[0], -house.facing[1]] } : lot;
}

/* The claimed cells by the house they were given to, and whether each is to be built: its owner's plot use builds it and no woodcut yard holds it. */
function toBuildByOwner(
  claims: Claims,
  uses: readonly (Use | null)[],
  inYard: (p: Vec2) => boolean
): { owned: Map<number, number[]>; build: Uint8Array } {
  const owned = new Map<number, number[]>();
  const build = new Uint8Array(claims.owner.length);
  for (const k of claims.cells) {
    const owner = claims.owner[k]!;
    const use = uses[owner];
    const at = centreOf(claims.grid, k);
    if (!use || !builds(use, at) || inYard(at)) continue;
    build[k] = 1;
    const list = owned.get(owner);
    if (list) list.push(k);
    else owned.set(owner, [k]);
  }
  return { owned, build };
}

/**
 * Buildings on the claimed ground: for each house in turn, rectangles square to it over the cells
 * its plot's use builds and that its neighbours' uses build too, none in a yard the woodcut shows.
 */
export function claimBuildings(
  claims: Claims,
  plots: readonly (ClaimPlot | null)[],
  site: ClaimSite,
  startIndex: number
): Lot[] {
  const { grid } = claims;
  const { placer } = site;
  const uses = plots.map(useOf);
  const { owned, build } = toBuildByOwner(claims, uses, site.inYard);
  const lots: Lot[] = [];
  for (const h of [...owned.keys()].sort((a, b) => a - b)) {
    const own = owned.get(h)!.filter((k) => build[k]);
    if (own.length === 0) continue;
    const use = uses[h]!;
    const house = plots[h]!.house;
    const frame = frameOf(house);
    const member = (p: Vec2) => {
      const k = cellAt(grid, p);
      return k >= 0 && build[k] === 1 && builds(use, p);
    };
    let count = 0;
    const extent = extentOf(
      frame,
      own.map((k) => centreOf(grid, k))
    );
    for (const rect of rectanglesIn(frame, extent, member, CLAIM.cell, CLAIM.least)) {
      const obb = snapped(rect, placer, member);
      if (!obb) continue;
      const lot = claimLot(house, ++count, obb, startIndex + lots.length, site.water);
      lots.push(lot);
      placer.add(lot);
      eachCellInObb(grid, obb, (k) => (build[k] = 0));
    }
  }
  return lots;
}
